/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import * as React from 'react';

import { HtmlHeadInclude, IAny, ICoreContext, IGeneric } from '@msdyn365-commerce/core';
import { IMfrmProductPageSummaryData } from './mfrm-product-page-summary.data';
import { IMfrmProductPageSummaryProps } from './mfrm-product-page-summary.props.autogenerated';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { removeSpecialCharsFromURLPath } from '../../Utilities/allRegex';
/**
 *
 * ProductPageSummary component
 * @extends {React.PureComponent<IMfrmProductPageSummaryProps>}
 */
class ProductPageSummary extends React.PureComponent<IMfrmProductPageSummaryProps<IMfrmProductPageSummaryData>> {
    constructor(props: any) {
        super(props);
    }

    public render(): JSX.Element {
        if (this.props.data.productPageSummary.result === undefined) {
            throw new Error('PageSummaryData input to page-summary is undefined. Unable to generate page-summary');
        }
        const {
            config,
            context,
            data
        } = this.props;
        let { data: { productPageSummary: { result: {
            title,
            description,
            keywords,
            sharingImageUrl,
            faviconUrl
        } } } } = this.props;

        // Product SEO metadata specific information - non-overridable
        const productName = title;

        // Override values coming from data action if config values are provided
        title = config.title?.split('<DEFAULT_PRODUCT_NAME>').join(productName) || title;
        description = config.description?.split('<DEFAULT_PRODUCT_NAME>').join(productName) || description;
        keywords = config.keywords || keywords;
        sharingImageUrl = (config.sharingImage && config.sharingImage.src) || sharingImageUrl;
        faviconUrl = config.faviconUrl || faviconUrl;

        // Construct page title with suffix and prefix if provided from app settings
        const pageTitle = this._constructPageTitle(title, context && context.request && context.request.app);
        const url = context.request.url.requestUrl;
        const urlParams = new URLSearchParams(url.search);
        const variantid = urlParams.get('variantid') ? `?variantid=${urlParams.get('variantid')}` : null;
        const canonicalUrl = `${url.origin}${removeSpecialCharsFromURLPath(url?.pathname, context)}${variantid ? variantid : ''}`;
        return (
            <React.Fragment>
                <HtmlHeadInclude>
                    {pageTitle && <title>{pageTitle}</title>}
                    {description && <meta name='description' content={description} />}
                    {keywords && <meta name='keywords' content={keywords} />}
                    {canonicalUrl && <link rel='canonical' href={canonicalUrl} />}
                    {faviconUrl && <link rel='shortcut icon' href={faviconUrl} />}
                    {this._renderProductMetadata(context, data)}
                    {!config.disableOgTags && this._renderOgTags(pageTitle, description, sharingImageUrl)}
                    {!config.disableTwitterTags && this._renderTwitterTags(pageTitle, description, sharingImageUrl)}
                </HtmlHeadInclude>
            </React.Fragment>
        );
    }

    /**
     * Renders the facebook and other social media specific metadata tags
     *
     * @param title Page title
     * @param description Product description
     * @param sharingImageUrl Primary product image url
     */
    private _renderOgTags(title: string | undefined, description: string | undefined, sharingImageUrl: string | undefined): JSX.Element {
        return (
            <>
                {title && <meta property='og:title' content={title} />}
                {description && <meta property='og:description' content={description} />}
                {sharingImageUrl && <meta property='og:image' content={sharingImageUrl} />}
                <meta property='og:type' content='website' />
            </>
        );
    }

    /**
     * Constructs and renders the JSON-LD tag used to output product specific metadata used by search engine crawlers
     * also known as: SEO Schema
     *
     * @param context used to get currency
     * @param data the resulting data gathered from coresponding action file.
     */
    private _renderProductMetadata(context: ICoreContext, data: IMfrmProductPageSummaryData): JSX.Element {
        if (!data.productPageSummary.result || !data.product.result || !context.request.channel?.Currency) {
            throw new Error("Missing data required to build SEO schema.");
        }

        const variantRecordId = data.product.result.RecordId;
        const currentVariant = data.productRetailId.result?.find((variant: SimpleProduct) => variant.RecordId === variantRecordId);
        const retailVariantId: string | undefined = currentVariant?.ExtensionProperties?.find(item => item.Key === 'RetailVariantId')?.Value?.StringValue;
        const {
            title,
            description,
            sharingImageUrl,
            canonicalUrl,
            variantId,
            price,
            productBrand,
            itemCondition,
            priceValidUntil,
            isOutOfStockArray,
            gtinArray
        } = data.productPageSummary.result;
        const currencyCode = context.request.channel.Currency;

        const isOutOfStockValue = isOutOfStockArray?.find(it => it.key === `${variantRecordId}`)?.value || null;
        const isOutOfStock = isOutOfStockValue === null ? undefined : parseInt(isOutOfStockValue, 10);
        const availability = isOutOfStock === undefined || isNaN(isOutOfStock) ?
            undefined : isOutOfStock ? "OutOfStock" : "InStock";

        const gtinValue = gtinArray?.find(it => it.key === `${retailVariantId}`)?.value || null;
        const gtin = gtinValue === null ? undefined : parseInt(gtinValue, 10) || undefined;

        // Construct the JSON-LD data that contains the product metadata information used by search enginge crawlers
        const productMetaData = JSON.stringify({
            // tslint:disable-next-line:no-http-string
            '@context': 'http://schema.org',
            '@type': 'Product',
            '@id': canonicalUrl,
            name: title,
            description: description,
            image: sharingImageUrl,
            sku: variantId?.toString(),
            gtin12: gtin?.toString(),
            itemCondition: itemCondition,
            brand: {
                '@type': 'Brand',
                name: productBrand
            },
            offers: {
                '@type': 'Offer',
                url: canonicalUrl,
                priceCurrency: currencyCode,
                price: price,
                priceValidUntil: priceValidUntil,
                availability: availability
            }
        });

        return (
            <>
                {/* tslint:disable:react-no-dangerous-html - intentionally setting inner html with only data action output (no unsanizted user input)*/}
                <script data-load-point='headEnd' type='application/ld+json' dangerouslySetInnerHTML={{ __html: productMetaData }} />
            </>
        );
    }

    /**
     * Renders the twitter specific metadata tags
     * @param title Page title
     * @param description Product description
     * @param sharingImageUrl Primary product image url
     */
    private _renderTwitterTags(
        title: string | undefined,
        description: string | undefined,
        sharingImageUrl: string | undefined
    ): JSX.Element {
        return (
            <>
                {title && <meta name='twitter:title' content={title} />}
                {description && <meta name='twitter:description' content={description} />}
                {sharingImageUrl && <meta name='twitter:image' content={sharingImageUrl} />}
                {<meta name='twitter:card' content='summary' />}
            </>
        );
    }

    /**
     * Constructs the page title using the prefix and suffix as defined in app settings and the product name
     * @param title The product name
     * @param app App settings
     */
    private _constructPageTitle(title: string | undefined, app: IGeneric<IAny>): string | undefined {
        if (!title) {
            title = '';
        }
        const moduleConfig = this.props.config;
        if (moduleConfig && moduleConfig.ignoreTitleSuffixPrefix) {
            return title;
        }
        const appConfig = app && app.platform;
        if (appConfig) {
            if (appConfig.pageTitlePrefix) {
                title = `${appConfig.pageTitlePrefix}${title}`;
            }
            if (appConfig.pageTitleSuffix) {
                title = `${title}${appConfig.pageTitleSuffix}`;
            }
        }

        return title;
    }
}

export default ProductPageSummary;
